import React from 'react';
import { Eye, Edit, Trash2, Send, Calendar, Users, AlertTriangle } from 'lucide-react';

const NotificationCard = ({ notification, onView, onEdit, onDelete, onSend }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeBadge = () => {
    const types = {
      'INFO': { bg: 'bg-blue-100', text: 'text-blue-800', icon: '📢' },
      'AVERTISSEMENT': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '⚠️' },
      'URGENCE': { bg: 'bg-red-100', text: 'text-red-800', icon: '🚨' },
      'FELICITATION': { bg: 'bg-green-100', text: 'text-green-800', icon: '🎉' },
      'RAPPEL': { bg: 'bg-purple-100', text: 'text-purple-800', icon: '⏰' },
      'EVENEMENT': { bg: 'bg-orange-100', text: 'text-orange-800', icon: '📅' }
    };
    
    const type = types[notification.type_notification] || types['INFO'];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${type.bg} ${type.text}`}>
        <span className="mr-1">{type.icon}</span>
        {notification.type_notification_display}
      </span>
    );
  };

  const getPrioriteBadge = () => {
    const priorites = {
      'BASSE': { bg: 'bg-gray-100', text: 'text-gray-800' },
      'NORMALE': { bg: 'bg-blue-100', text: 'text-blue-800' },
      'HAUTE': { bg: 'bg-orange-100', text: 'text-orange-800' },
      'CRITIQUE': { bg: 'bg-red-100', text: 'text-red-800' }
    };
    
    const priorite = priorites[notification.priorite] || priorites['NORMALE'];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorite.bg} ${priorite.text}`}>
        {notification.priorite_display}
      </span>
    );
  };

  const getStatusBadge = () => {
    if (notification.est_envoyee) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          ✓ Envoyée
        </span>
      );
    } else if (notification.est_programmee) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          🕐 Programmée
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          ⏸ En attente
        </span>
      );
    }
  };

  return (
    <div className="group bg-white rounded-xl border border-gray-200 hover:border-[#FF6B00]/30 hover:shadow-xl transition-all duration-300 overflow-hidden">
      {/* Barre de couleur selon priorité */}
      <div className={`h-1.5 ${
        notification.priorite === 'CRITIQUE' ? 'bg-red-500' :
        notification.priorite === 'HAUTE' ? 'bg-orange-500' :
        notification.priorite === 'NORMALE' ? 'bg-blue-500' :
        'bg-gray-400'
      }`} />

      <div className="p-5">
        {/* En-tête avec badges */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {getTypeBadge()}
              {getPrioriteBadge()}
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#FF6B00] transition-colors line-clamp-1">
              {notification.titre}
            </h3>
          </div>
          {getStatusBadge()}
        </div>

        {/* Message */}
        <p className="text-sm text-gray-600 mb-4 line-clamp-3 leading-relaxed">
          {notification.message}
        </p>

        {/* Statistiques visuelles */}
        {notification.est_envoyee && notification.nombre_destinataires > 0 && (
          <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-600">Taux de lecture</span>
              <span className="text-sm font-bold text-indigo-600">
                {Math.round((notification.nombre_lectures / notification.nombre_destinataires) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${(notification.nombre_lectures / notification.nombre_destinataires) * 100}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
              <span>{notification.nombre_lectures} lecture{notification.nombre_lectures > 1 ? 's' : ''}</span>
              <span>{notification.nombre_destinataires} destinataire{notification.nombre_destinataires > 1 ? 's' : ''}</span>
            </div>
          </div>
        )}

        {/* Informations compactes */}
        <div className="grid grid-cols-1 gap-2 mb-4">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Users className="w-3.5 h-3.5 text-[#FF6B00]" />
            <span className="font-medium">{notification.type_cible_display}</span>
            {notification.nombre_destinataires > 0 && (
              <span className="text-gray-400">• {notification.nombre_destinataires} personne{notification.nombre_destinataires > 1 ? 's' : ''}</span>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Calendar className="w-3.5 h-3.5 text-[#FF6B00]" />
            <span>{formatDate(notification.created_at)}</span>
          </div>

          {notification.est_envoyee && notification.date_envoi && (
            <div className="flex items-center gap-2 text-xs text-green-600">
              <Send className="w-3.5 h-3.5" />
              <span>Envoyée le {formatDate(notification.date_envoi)}</span>
            </div>
          )}

          {notification.est_programmee && notification.date_envoi_programmee && !notification.est_envoyee && (
            <div className="flex items-center gap-2 text-xs text-blue-600">
              <Calendar className="w-3.5 h-3.5" />
              <span>Programmée: {formatDate(notification.date_envoi_programmee)}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-4 border-t border-gray-100">
          <button
            onClick={() => onView(notification)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-lg hover:from-blue-100 hover:to-blue-200 transition-all duration-200"
            title="Voir les détails"
          >
            <Eye className="w-4 h-4" />
            <span className="hidden sm:inline">Voir</span>
          </button>

          {!notification.est_envoyee && (
            <>
              <button
                onClick={() => onEdit(notification)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium bg-gradient-to-r from-orange-50 to-orange-100 text-[#FF6B00] rounded-lg hover:from-orange-100 hover:to-orange-200 transition-all duration-200"
                title="Modifier"
              >
                <Edit className="w-4 h-4" />
                <span className="hidden sm:inline">Modifier</span>
              </button>
              
              <button
                onClick={() => onSend(notification.id)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium bg-gradient-to-r from-green-50 to-green-100 text-green-700 rounded-lg hover:from-green-100 hover:to-green-200 transition-all duration-200"
                title="Envoyer maintenant"
              >
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">Envoyer</span>
              </button>
            </>
          )}

          <button
            onClick={() => onDelete(notification.id)}
            className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium bg-gradient-to-r from-red-50 to-red-100 text-red-600 rounded-lg hover:from-red-100 hover:to-red-200 transition-all duration-200"
            title="Supprimer"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;
